import { Button, Typography } from '@mui/material';
import React from 'react';
import Compress from 'compress.js';
import Lottie from 'lottie-react';
import PropTypes from 'prop-types';
import {
  BREACH_CHECKED,
  MainStyles,
  OFFLINE_BODY_BCK,
  ORIGINAL_VIAL_BODY,
  STATUS_SKIPPED,
} from '../constants';
import { CalculateScannedCount } from '../helpers/calculateScanCount';
import { UpdateVialBody } from '../../../services/medicService';
import lottieLoading from '../../../static/lottie-loading-orange.json';
import { offlineStore } from '../../../store/offlineStore';
import { b64toBlob, generateBarcodeName } from '../../../helpers/miscHelpers';
import { UploadToS3 } from '../../../services/tertiaryService';
import { documentsCollectionPending } from '../Plugs/CollectionPrereqs';

const PrimaryCTA = ({
  success,
  parentData,
  documentData,
  setUpdate,
  update,
  setSuccess,
  taskId,
  setErrorAlert,
  setCloseAlert,
  isOffline,
  isEclinic,
  campId,
}) => {
  const compress = new Compress();
  const [loading, setLoading] = React.useState(false);
  const offlineStoreData = offlineStore((s) => s.actions);

  if (isOffline) {
    return (
      <Button
        sx={MainStyles.DoneButton}
        onClick={() => {
          if (navigator.onLine) {
            if (!loading) {
              setLoading(true);
              const date = new Date();
              const currentBody = JSON.parse(localStorage.getItem(ORIGINAL_VIAL_BODY));
              const offlineBodyBck = JSON.parse(localStorage.getItem(OFFLINE_BODY_BCK + taskId));
              const IdsScanned = Object.keys(offlineBodyBck);

              // An array to store promises for UploadToS3 calls
              const uploadPromises = [];

              // Update vial objects
              currentBody.orders.forEach((order) => {
                order.vials.forEach((vial) => {
                  if (IdsScanned.indexOf(String(vial.accession_id)) !== -1) {
                    vial.barcode = offlineBodyBck[vial.accession_id];
                    vial.reason_for_skip = STATUS_SKIPPED;
                    vial.reason_custom = '';
                    vial.reason_timestamp = null;
                    vial.barcode_scanned_at = date.toISOString();
                    if (vial.accession_id in offlineStoreData) {
                      // Create a promise for each UploadToS3 call and push it to the array
                      const uploadPromise = new Promise((resolve) => {
                        compress.compress([b64toBlob(offlineStoreData[vial.accession_id])], {
                          quality: 0.5,
                        }).then((data) => {
                          const img = data[0];
                          const base64str = img.data;
                          const imgExt = img.ext;
                          const compressed = Compress.convertBase64ToFile(base64str, imgExt);
                          UploadToS3(
                            compressed,
                            generateBarcodeName(String(offlineBodyBck[vial.accession_id])),
                            'vial',
                          )
                            .then((r) => {
                              vial.image_url = r.mediaUrl;
                              resolve(); // Resolve the promise when UploadToS3 completes
                            })
                            .catch(() => {
                              vial.image_url = 'Unavailable: Upload fail';
                              resolve(); // Resolve even if there's an error
                            });
                        }).catch(() => {
                          vial.image_url = 'Unavailable: Compression error';
                          resolve(); // Resolve even if there's an error
                        });
                      });
                      uploadPromises.push(uploadPromise);
                    } else {
                      vial.image_url = 'Unavailable: Refresh state';
                    }
                  }
                });
              });
              // Use Promise.all to wait for all UploadToS3 calls to complete
              Promise.all(uploadPromises)
                .then(() => {
                  // Update vial body
                  UpdateVialBody(taskId, currentBody)
                    .then(() => {
                      localStorage.removeItem(OFFLINE_BODY_BCK + taskId);
                      setLoading(false);
                      window.location.reload();
                    })
                    .catch(() => {
                      localStorage.removeItem(OFFLINE_BODY_BCK + taskId);
                      setLoading(false);
                      setErrorAlert([true, 'Duplicate barcode scanned']);
                      setTimeout(() => {
                        window.location.reload();
                      }, 2000);
                    });
                })
                .catch(() => {
                  setLoading(false);
                  setErrorAlert([true, 'Error uploading image']);
                });
            }
          } else {
            setErrorAlert([true, 'No internet connection detected']);
          }
        }}
      >
        <Typography
          sx={loading ? MainStyles.DoneTextLoading : MainStyles.DoneText}
        >
          {
            loading
              ? (
                <Lottie
                  animationData={lottieLoading}
                  loop
                  height={60}
                  width={60}
                  className="lottie-animation"
                />
              )
              : `Upload saved data`
          }
        </Typography>
      </Button>
    );
  }
  return (
    <Button
      sx={!success ? MainStyles.SubmitButton : MainStyles.DoneButton}
      onClick={() => {
        if (!success) {
          const { orders = [] } = parentData;
          const barcodeCount = CalculateScannedCount(orders);
          if (barcodeCount >= parentData?.total_vials) {
            if (documentsCollectionPending(documentData)) {
              setLoading(false);
              setErrorAlert([true, 'Please fill required documents!']);
              return;
            }
            if (parentData && !loading) {
              setLoading(true);
              const newData = parentData;
              newData.form_completed = true;
              UpdateVialBody(taskId, newData, isEclinic && campId).then(() => {
                setUpdate(!update);
                setSuccess(true);
                if (taskId) {
                  localStorage.removeItem(BREACH_CHECKED + taskId);
                }
                window.scrollTo(0, 0);
                setLoading(false);
              }).catch(() => {
                setLoading(false);
                setErrorAlert([true, 'Error submitting form']);
              });
            }
          } else {
            setLoading(false);
            setErrorAlert([true, 'Scan/record all vials']);
          }
        } else {
          setLoading(false);
          setCloseAlert(true);
        }
      }}
    >
      <Typography
        sx={!success ? MainStyles.SubmitText : MainStyles.DoneText}
      >
        {!success ? 'Proceed' : 'Done'}
      </Typography>
    </Button>
  );
};

export default PrimaryCTA;

PrimaryCTA.propTypes = {
  isOffline: PropTypes.bool,
  success: PropTypes.bool,
  parentData: PropTypes.object || PropTypes.array,
  documentData: PropTypes.object,
  setUpdate: PropTypes.func,
  update: PropTypes.bool,
  setSuccess: PropTypes.func,
  taskId: PropTypes.string || PropTypes.number,
  setErrorAlert: PropTypes.func,
  setCloseAlert: PropTypes.func,
  isEclinic: PropTypes.bool,
  campId: PropTypes.string || PropTypes.number,
};
